import React from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  FollowingUserCard,
  MarketplaceEmptyState,
} from '@/components/residents/marketplace';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
} from '@/utils/marketplace';
import { useGetFollowingQuery, useToggleFollowMutation } from '@/store/api';

type MarketplaceFollowingNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceFollowing'>;

export const MarketplaceFollowingScreen: React.FC = () => {
  const navigation = useNavigation<MarketplaceFollowingNavigationProp>();
  const { layoutMode, isWeb } = useResponsive();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Fetch following users from API
  const { 
    data: followingUsers = [], 
    isLoading,
  } = useGetFollowingQuery();

  // Toggle follow mutation
  const [toggleFollow] = useToggleFollowMutation();

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleFollowToggle = async (userId: string) => {
    try {
      await toggleFollow(userId).unwrap();
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  };

  const handleProfilePress = (userId: string) => {
    navigation.navigate('SellerProfile', { sellerId: userId });
  };

  return (
    <MarketplaceScreenLayout navigatorTitle="Following">
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: contentPadding },
          !isMobile && styles.contentLarge,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Responsive content wrapper */}
        <View style={[
          styles.contentWrapper,
          maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
        ]}>
          {/* Back Navigation */}
          <BreadcrumbNavigation 
            onHomePress={handleGoToMarketplace}
            onBackPress={handleGoBack}
          />

          {/* Following Users List */}
          <View style={styles.usersContainer}>
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
              </View>
            ) : followingUsers.length > 0 ? (
              followingUsers.map((user) => (
                <FollowingUserCard
                  key={user.id}
                  user={user}
                  onFollowToggle={handleFollowToggle}
                  onProfilePress={handleProfilePress}
                />
              ))
            ) : (
              <MarketplaceEmptyState
                title="Not following anyone yet"
                subtitle="Follow sellers to see their new listings"
              />
            )}
          </View>
        </View>
      </ScrollView>
    </MarketplaceScreenLayout>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 20,
  },
  contentLarge: {
    gap: 28,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 20,
  },
  usersContainer: {
    gap: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
});
